// pages/Manufacturers.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  getManufacturers, 
  createManufacturer, 
  updateManufacturer, 
  deactivateManufacturer,
  activateManufacturer
} from '../services/manufacturerService';
import { getIcon } from '../utils/icons';
import { toast } from 'react-toastify';

const Manufacturers = () => {
  const initialPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10);

  // Состояния
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingManufacturer, setEditingManufacturer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    country: '',
    website: ''
  });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [page, setPage] = useState(initialPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [countries, setCountries] = useState([]);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showInactive, setShowInactive] = useState(true); // По умолчанию показываем неактивные

  // Загрузка производителей при монтировании компонента и изменении страницы
  useEffect(() => {
    fetchManufacturers(page);
    const params = new URLSearchParams();
    if (page > 1) params.set('page', page.toString());
    const newUrl = window.location.pathname + (params.toString() ? `?${params.toString()}` : '');
    window.history.replaceState({}, '', newUrl);
  }, [page]);

  const filtersInitialized = useRef(false);

  // Обработка изменения поисковых фильтров с задержкой
  useEffect(() => {
    if (!filtersInitialized.current) {
      filtersInitialized.current = true;
      return;
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    setSearchTimeout(setTimeout(() => {
      setPage(1);
      fetchManufacturers(1);
    }, 500));

    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTerm, countryFilter, showInactive]);

  // Функция загрузки всех производителей
  const fetchManufacturers = async (pageNumber) => {
    try {
      setLoading(true);
      
      // Получаем данные с реального API, передавая параметр для включения неактивных
      const data = await getManufacturers({}, showInactive);
      
      // Получение уникальных стран для фильтра
      const uniqueCountries = [...new Set(data
        .filter(m => m.country) // только непустые значения
        .map(m => m.country))];
      setCountries(uniqueCountries);
      
      // Фильтрация по поисковому запросу, если есть
      let filteredManufacturers = data;
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        filteredManufacturers = filteredManufacturers.filter(m => 
          (m.name && m.name.toLowerCase().includes(searchLower)) || 
          (m.description && m.description.toLowerCase().includes(searchLower)) || 
          (m.website && m.website.toLowerCase().includes(searchLower))
        );
      }
      
      // Фильтрация по стране, если выбрана
      if (countryFilter) {
        filteredManufacturers = filteredManufacturers.filter(m => 
          m.country === countryFilter
        );
      }
      
      setManufacturers(filteredManufacturers);
      
      // Расчет пагинации (предполагаем 10 элементов на страницу)
      const itemsPerPage = 10;
      setTotalPages(Math.max(1, Math.ceil(filteredManufacturers.length / itemsPerPage)));
      setPage(pageNumber);
      
    } catch (error) {
      console.error('Ошибка загрузки производителей:', error);
      toast.error('Не удалось загрузить производителей');
    } finally {
      setLoading(false);
    }
  };

  // Открытие модального окна для добавления/редактирования
  const handleOpenModal = (manufacturer = null) => {
    if (manufacturer) {
      setEditingManufacturer(manufacturer);
      setFormData({
        name: manufacturer.name,
        description: manufacturer.description || '',
        country: manufacturer.country || '',
        website: manufacturer.website || ''
      });
    } else {
      setEditingManufacturer(null);
      setFormData({
        name: '',
        description: '',
        country: '',
        website: ''
      });
    }
    setShowModal(true);
  };

  // Закрытие модального окна
  const handleCloseModal = () => {
    setShowModal(false);
    setEditingManufacturer(null);
  };

  // Обработка изменения полей формы
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Отправка формы (создание/обновление производителя)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let result;
      
      if (editingManufacturer) {
        // Обновление существующего производителя
        result = await updateManufacturer(editingManufacturer.id, formData);
        toast.success('Производитель успешно обновлен');
      } else {
        // Создание нового производителя
        result = await createManufacturer(formData);
        toast.success('Производитель успешно создан');
      }
      
      // Перезагружаем список производителей
      fetchManufacturers(page);
      handleCloseModal();
    } catch (error) {
      console.error('Ошибка сохранения производителя:', error);
      toast.error(error.response?.data?.message || 'Не удалось сохранить производителя');
    }
  };

  // Открытие диалога подтверждения удаления
  const openDeleteConfirm = (id) => {
    setConfirmDelete(id);
  };

  // Закрытие диалога подтверждения удаления
  const closeDeleteConfirm = () => {
    setConfirmDelete(null);
  };

  // Обработка деактивации производителя
  const handleDeactivate = async (id) => {
    try {
      // Используем специальный метод DELETE для деактивации
      await deactivateManufacturer(id);
      fetchManufacturers(page);
      toast.success('Производитель успешно деактивирован');
      if (confirmDelete) closeDeleteConfirm();
    } catch (error) {
      console.error('Ошибка деактивации производителя:', error);
      toast.error('Не удалось деактивировать производителя');
    }
  };
  
  // Обработка активации производителя
  const handleActivate = async (id) => {
    try {
      // Находим производителя в списке
      const manufacturer = manufacturers.find(m => m.id === id);
      if (!manufacturer) {
        toast.error('Производитель не найден');
        return;
      }
      
      // Активируем производителя, передавая все его текущие данные
      await activateManufacturer(id, {
        name: manufacturer.name,
        description: manufacturer.description || '',
        country: manufacturer.country || '',
        website: manufacturer.website || ''
      });
      
      fetchManufacturers(page);
      toast.success('Производитель успешно активирован');
    } catch (error) {
      console.error('Ошибка активации производителя:', error);
      toast.error('Не удалось активировать производителя');
    }
  };

  // Обработка удаления производителя (для диалога подтверждения)
  const handleDelete = async (id) => {
    try {
      // Для деактивации используем специальный метод
      await deactivateManufacturer(id);
      fetchManufacturers(page);
      toast.success('Производитель успешно деактивирован');
      closeDeleteConfirm();
    } catch (error) {
      console.error('Ошибка деактивации производителя:', error);
      toast.error('Не удалось деактивировать производителя');
    }
  };

  return (
    <div>
      <header className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Производители</h1>
          <p className="text-gray-600">Управление производителями товаров</p>
        </div>
        
        <div className="mt-4 md:mt-0">
          <button
            onClick={() => handleOpenModal()}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md flex items-center"
          >
            {getIcon('plus')}
            <span className="ml-2">Добавить производителя</span>
          </button>
        </div>
      </header>
      
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  {getIcon('search')}
                </div>
                <input
                  type="text"
                  placeholder="Поиск по названию, описанию, сайту..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="flex-1 md:max-w-xs">
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
              >
                <option value="">Все страны</option>
                {countries.map((country, index) => (
                  <option key={index} value={country}>{country}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-center">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                <span className="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300">Показывать неактивные</span>
              </label>
            </div>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Название
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Страна
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Веб-сайт
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Статус
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Действия
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {manufacturers.length > 0 ? (
                  manufacturers.map((manufacturer) => (
                    <tr key={manufacturer.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{manufacturer.name}</div>
                        {manufacturer.description && (
                          <div className="text-sm text-gray-500">{manufacturer.description}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">{manufacturer.country || '-'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {manufacturer.website ? (
                            <a 
                              href={manufacturer.website.startsWith('http') ? manufacturer.website : `https://${manufacturer.website}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-500 hover:underline"
                            >
                              {manufacturer.website}
                            </a>
                          ) : (
                            '-'
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            manufacturer.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {manufacturer.isActive ? 'Активен' : 'Неактивен'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleOpenModal(manufacturer)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Редактировать"
                          >
                            {getIcon('edit')}
                          </button>
                          {manufacturer.isActive ? (
                            <button
                              onClick={() => openDeleteConfirm(manufacturer.id)}
                              className="text-red-600 hover:text-red-900"
                              title="Деактивировать"
                            >
                              {getIcon('block')}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleActivate(manufacturer.id)}
                              className="text-green-600 hover:text-green-900"
                              title="Активировать"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-sm font-medium text-gray-500">
                      Производители не найдены. Добавьте первого производителя!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Страница {page} из {totalPages}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => page > 1 && setPage(page - 1)}
                disabled={page === 1}
                className={`px-3 py-1 rounded-md ${page === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              >
                Назад
              </button>
              <button
                onClick={() => page < totalPages && setPage(page + 1)}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded-md ${page === totalPages ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-100'}`}
              >
                Далее
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Модальное окно для добавления/редактирования производителя */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">
              {editingManufacturer ? 'Редактирование производителя' : 'Добавление производителя'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                  Название *
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Введите название производителя"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="description">
                  Описание
                </label>
                <textarea
                  id="description"
                  name="description"
                  placeholder="Введите описание"
                  value={formData.description}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="country">
                  Страна
                </label>
                <input
                  id="country"
                  name="country"
                  type="text"
                  placeholder="Введите страну"
                  value={formData.country}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="website">
                  Веб-сайт
                </label>
                <input
                  id="website"
                  name="website"
                  type="text"
                  placeholder="Введите URL сайта"
                  value={formData.website}
                  onChange={handleChange}
                  className="border border-gray-300 rounded-md w-full py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
                >
                  Сохранить
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Диалог подтверждения удаления */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-4">Подтверждение деактивации</h3>
            <p className="mb-6">Вы уверены, что хотите деактивировать этого производителя? Это может повлиять на связанные товары.</p>
            <div className="flex justify-end">
              <button
                onClick={closeDeleteConfirm}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              >
                Отмена
              </button>
              <button
                onClick={() => handleDelete(confirmDelete)}
                className="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded"
              >
                Деактивировать
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Manufacturers;